import { inject } from '@angular/core';
import { map, tap } from 'rxjs';
import { patchState, signalStore, withMethods } from '@ngrx/signals';
import {
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import { withCursorPagination } from '@dgacontrol-frontend/shared-util-cursor-pagination';

import { withChargesListFilters } from './charges-list-filters.feature';
import { BillingWorkChargeService } from './generated/billing-work-charge/billing-work-charge.service';
import type {
  BillingV1WorkChargesListParams,
  PatchedWorkChargeUpdateRequestV1Request,
  WorkChargeCreateRequestV1Request,
  WorkChargePreviewRequestV1Request,
  WorkChargeV1,
} from './generated/types';

/**
 * TARG-4 pure: injects the generated services directly, no facade. Same shape as
 * `BillingItemsStore` + `SubscriptionsStore` + `SubscriptionChargesStore` (iters 1-3).
 *
 * One store for both kinds of work charge (M-bill-36): installation and service.
 * `kind` goes in the create body and narrows the listing (`?kind=`).
 *
 * This store owns the CHARGE only. Lines stopped being a field of the charge (M-bill-4):
 * they are their own resource, handled by the component-scoped `WorkChargeLinesStore`.
 * What stays here is the parent entity — every line mutation answers with the whole
 * charge and its recomputed `total_net`, which reaches the listing through `syncCharge`
 * (or `refreshCharge` for the DELETE, which answers 204 with no body).
 *
 * COMPONENT-DRIVEN: `dt-work-charges` calls `load(query)` in its own `ngOnInit` with
 * the current filters; the store does NOT auto-load.
 */
export const WorkChargesStore = signalStore(
  withEntities<WorkChargeV1>(),
  withCursorPagination<WorkChargeV1, BillingV1WorkChargesListParams>(() => {
    const api = inject(BillingWorkChargeService);
    // The generated page already is { results, next, previous }: no adapter.
    return (query, cursor) =>
      api.billingV1WorkChargesList({ ...query, cursor: cursor ?? undefined });
  }),
  withChargesListFilters(),
  withMethods((store, api = inject(BillingWorkChargeService)) => {
    // Every line mutation answers with the WHOLE parent charge, `total_net` recomputed
    // server-side. The lines store owns those calls and hands the charge here so the
    // listing behind the modal shows the new total. Pure state patch, no HTTP.
    const syncCharge = (charge: WorkChargeV1) => {
      patchState(store, updateEntity({ id: charge.id, changes: charge }));
    };

    return {
      // POST → single `{work_charge}`. Do NOT addEntity into a cursor list: the new
      // row's server-side sort position is unknown → inserting locally desyncs the
      // cursor → dup/missing rows on the next loadMore. Reset to page 1 instead.
      createWorkCharge(body: WorkChargeCreateRequestV1Request) {
        return api.billingV1WorkChargesCreate(body).pipe(
          tap(() => {
            store.load({});
          }),
          map((res) => res.work_charge),
        );
      },
      // Stateless by contract: computes what the charge WOULD total and persists
      // nothing, so there is no entity to patch. Exists because the arithmetic is
      // three HALF_UP roundings over Decimal and a float64 copy of it disagrees by
      // one peso on the values that land on .5.
      previewWorkCharge(body: WorkChargePreviewRequestV1Request) {
        return api.billingV1WorkChargesPreviewsCreate(body);
      },
      // PATCH → single `{work_charge}` → patch the row IN PLACE (no reorder, cursor
      // stays valid). Line edits go through the per-line CRUD below.
      updateWorkCharge(
        body: PatchedWorkChargeUpdateRequestV1Request,
        identity: string,
      ) {
        return api.billingV1WorkChargesPartialUpdate({ identity }, body).pipe(
          tap((res) => {
            patchState(
              store,
              updateEntity({
                id: res.work_charge.id,
                changes: res.work_charge,
              }),
            );
          }),
          map((res) => res.work_charge),
        );
      },
      deleteWorkCharge(identity: string, id: number) {
        return api.billingV1WorkChargesDestroy({ identity }).pipe(
          tap(() => {
            patchState(store, removeEntity(id));
          }),
        );
      },
      markPaid(identity: string, id: number) {
        return api.billingV1WorkChargesPaidMark({ identity }).pipe(
          tap((res) => {
            patchState(store, updateEntity({ id, changes: res.work_charge }));
          }),
          map((res) => res.work_charge),
        );
      },
      unmarkPaid(identity: string, id: number) {
        return api.billingV1WorkChargesPaidUnmark({ identity }).pipe(
          tap((res) => {
            patchState(store, updateEntity({ id, changes: res.work_charge }));
          }),
          map((res) => res.work_charge),
        );
      },
      syncCharge,
      // DELETE of a line answers 204 with no body — the only line mutation that cannot
      // hand us the charge, so the total has to be re-read.
      refreshCharge(identity: string) {
        return api.billingV1WorkChargesRetrieve({ identity }).pipe(
          map((res) => res.work_charge),
          tap(syncCharge),
        );
      },
      // GET → binary PDF (`responseType: 'blob'` in the generated client). Nothing
      // to patch: the PDF is rendered on the fly and never stored (M-bill-3).
      invoicePdf(identity: string) {
        return api.billingWorkChargeInvoicePdfRetrieve({ identity });
      },
    };
  }),
);
